import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TemplateEditorCanvas } from "../template-editor-v2/template-editor-canvas";
import { TemplateEditorV2Slideshow } from "../template-editor-v2-slideshow/template-editor-v2-slideshow";
import {
    DEFAULT_TEMPLATE_ACCENT_COLOR,
    TemplateDto,
    TemplateOrientation,
    TemplateSceneV1,
    TemplateSceneV2,
} from "@exibra/shared";
import { useMemo, useState } from "react";
import { useSaveTemplate } from "@/api/template.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { v4 as uuidv4 } from "uuid";

type Format = "v1" | "v2";

export const CreateTemplate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutateAsync: saveTemplate } = useSaveTemplate();
    const tenant = useTenantStore((state) => state.selectedTenant);

    const [format, setFormat] = useState<Format | null>(null);
    const [orientation, setOrientation] = useState<TemplateOrientation>("horizontal");

    const v1Creation: TemplateDto = useMemo(
        () =>
            ({
                id: 0,
                name: t("templates.newTemplate"),
                version: 1,
                scene: {
                    elements: [],
                    style: {
                        textColor: "",
                        backgroundColor: "",
                        cardBackgroundColor: "",
                        accentColor: DEFAULT_TEMPLATE_ACCENT_COLOR,
                    },
                } satisfies TemplateSceneV1,
                tenantId: tenant?.id ?? 0,
                createdAt: new Date(),
                updatedAt: new Date(),
            }) as TemplateDto,
        [t, tenant],
    );

    const v2Creation: TemplateDto = useMemo(() => {
        const dimensions =
            orientation === "horizontal"
                ? { width: 1920, height: 1080 }
                : { width: 1080, height: 1920 };
        const scene: TemplateSceneV2 = {
            orientation,
            dimensions,
            defaultPhotoDurationMs: 8000,
            loop: true,
            scenes: [
                {
                    id: uuidv4(),
                    name: t("templates.scene", { defaultValue: "Scene" }) + " 1",
                    durationMs: 8000,
                    elements: [],
                },
            ],
            style: {
                textColor: "",
                backgroundColor: "",
                cardBackgroundColor: "",
                accentColor: DEFAULT_TEMPLATE_ACCENT_COLOR,
            },
            checksum: "",
        };
        return {
            id: 0,
            name: t("templates.newTemplate"),
            version: 2,
            scene,
            tenantId: tenant?.id ?? 0,
            createdAt: new Date(),
            updatedAt: new Date(),
        } as TemplateDto;
    }, [orientation, t, tenant]);

    const onSave = async (template: TemplateDto) => {
        await saveTemplate({ template, isCreate: true });
        navigate("..");
    };

    if (!tenant) {
        return null;
    }

    if (format === null) {
        return (
            <>
                <title>{t("templates.createNew")}</title>
                <FormatPicker
                    orientation={orientation}
                    onOrientationChange={setOrientation}
                    onPick={setFormat}
                />
            </>
        );
    }

    if (format === "v2") {
        return (
            <>
                <title>{t("templates.createNew")}</title>
                <TemplateEditorV2Slideshow template={v2Creation} onSave={onSave} />
            </>
        );
    }

    return (
        <>
            <title>{t("templates.createNew")}</title>
            <TemplateEditorCanvas template={v1Creation} onSave={onSave} />
        </>
    );
};

const FormatPicker = ({
    orientation,
    onOrientationChange,
    onPick,
}: {
    orientation: TemplateOrientation;
    onOrientationChange: (value: TemplateOrientation) => void;
    onPick: (format: Format) => void;
}) => {
    const { t } = useTranslation();
    return (
        <div className="p-6 max-w-3xl mx-auto flex flex-col gap-6">
            <h1 className="font-heading text-2xl">
                {t("templates.format.title", { defaultValue: "Choose template format" })}
            </h1>
            <p className="text-sm text-muted-foreground">
                {t("templates.format.description", {
                    defaultValue:
                        "v1 templates are static and run on existing devices. v2 templates support slideshow + video and only run on updated presenters.",
                })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4 flex flex-col gap-3">
                    <h3 className="font-semibold">{t("templates.format.v1Title")}</h3>
                    <p className="text-sm text-muted-foreground flex-1">
                        {t("templates.format.v1Desc", {
                            defaultValue:
                                "Single 16:9 scene with text and images. Renders on every presenter.",
                        })}
                    </p>
                    <Button onClick={() => onPick("v1")}>
                        {t("templates.format.useV1", { defaultValue: "Create v1 template" })}
                    </Button>
                </Card>
                <Card className="p-4 flex flex-col gap-3">
                    <h3 className="font-semibold">{t("templates.format.v2Title")}</h3>
                    <p className="text-sm text-muted-foreground flex-1">
                        {t("templates.format.v2Desc", {
                            defaultValue:
                                "Multiple scenes with photo, video, and text layers. Loops infinitely. Requires an updated presenter.",
                        })}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            variant={orientation === "horizontal" ? "default" : "outline"}
                            onClick={() => onOrientationChange("horizontal")}
                            size="sm"
                        >
                            {t("templates.format.horizontal", {
                                defaultValue: "Horizontal (1920×1080)",
                            })}
                        </Button>
                        <Button
                            variant={orientation === "vertical" ? "default" : "outline"}
                            onClick={() => onOrientationChange("vertical")}
                            size="sm"
                        >
                            {t("templates.format.vertical", {
                                defaultValue: "Vertical (1080×1920)",
                            })}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {t("templates.format.orientationLocked", {
                            defaultValue:
                                "This sets the primary orientation. You can add the opposite orientation as a second version in the editor.",
                        })}
                    </p>
                    <Button onClick={() => onPick("v2")}>
                        {t("templates.format.useV2", { defaultValue: "Create v2 template" })}
                    </Button>
                </Card>
            </div>
        </div>
    );
};
